import { useState, useRef, useEffect } from "react";
import { ToolsShell, SensorNotice, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Square Checker — angle-only mode. Per the brief, skipping camera mode
// (edge detection is unreliable without OpenCV; angle mode covers 95% of
// use cases). User lays the phone against a corner; the tool measures
// the angle of the corner relative to a calibrated zero (the user
// establishes zero on one surface, then rotates to the perpendicular
// surface to read the angle between them).

const TARGET_ANGLE = 90;

export default function SquareChecker() {
  const [state, setState] = useState("idle");
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [refAngle, setRefAngle] = useState(null);
  const [refAxis, setRefAxis] = useState("y");
  const handler = useRef(null);

  const enable = async () => {
    if (typeof DeviceOrientationEvent === "undefined") { setState("unsupported"); return; }
    try {
      if (typeof DeviceOrientationEvent.requestPermission === "function") {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") { setState("denied"); return; }
      }
      handler.current = (e) => {
        setTilt({ x: e.gamma || 0, y: e.beta || 0 });
      };
      window.addEventListener("deviceorientation", handler.current, true);
      setState("ok");
    } catch {
      setState("denied");
    }
  };

  useEffect(() => () => {
    if (handler.current) window.removeEventListener("deviceorientation", handler.current, true);
  }, []);

  const setReference = () => {
    // Use whichever axis has the larger absolute value (most sensitive)
    const axis = Math.abs(tilt.y) > Math.abs(tilt.x) ? "y" : "x";
    setRefAxis(axis);
    setRefAngle(axis === "y" ? tilt.y : tilt.x);
  };
  const clear = () => { setRefAngle(null); };

  const currentVal = refAxis === "y" ? tilt.y : tilt.x;
  const measuredAngle = refAngle != null ? Math.abs(currentVal - refAngle) : null;
  const fromSquare = measuredAngle != null ? measuredAngle - TARGET_ANGLE : null;
  const isSquare = fromSquare != null && Math.abs(fromSquare) < 0.5;

  return (
    <ToolsShell title="Square Checker">
      {state !== "ok" && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p style={{ fontSize: 14, color: "#C8CADF", marginBottom: 18 }}>
            Check whether a corner is exactly 90°. Lay phone flat on one surface, tap "Reference here", then rotate phone onto the perpendicular surface to read the angle.
          </p>
          <button onClick={enable} style={primaryBtn}>Start</button>
          <SensorNotice state={state === "idle" ? "prompt" : state} what="Motion sensor" />
        </div>
      )}

      {state === "ok" && (
        <>
          {/* Visual indicator */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
            <svg width="240" height="200" viewBox="0 0 240 200">
              <line x1="20" y1="170" x2="220" y2="170" stroke="#262839" strokeWidth="2" />
              <line x1="120" y1="170" x2="120" y2="20" stroke="#262839" strokeWidth="2" strokeDasharray="3 4" />
              {measuredAngle != null && (
                <g transform={`rotate(${-(measuredAngle - 90)} 120 170)`}>
                  <line x1="120" y1="170" x2="120" y2="30" stroke={isSquare ? "#5BD99A" : TOOLS_ACCENT} strokeWidth="3" strokeLinecap="round" />
                </g>
              )}
              {/* Right-angle indicator at corner */}
              <path d="M 108 170 L 108 158 L 120 158" fill="none" stroke="#5B5E7A" strokeWidth="1.5" />
            </svg>
          </div>

          {refAngle == null ? (
            <div style={{ marginTop: 14, textAlign: "center" }}>
              <p style={{ fontSize: 13, color: "#C8CADF", marginBottom: 14 }}>
                Lay phone flat on the first surface, then tap below to set the reference.
              </p>
              <button onClick={setReference} style={primaryBtn}>Reference here</button>
            </div>
          ) : (
            <>
              <div style={{ marginTop: 14, background: "#13141F", border: `1px solid ${isSquare ? "#5BD99A" : TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 22px", textAlign: "center" }}>
                <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 4 }}>Measured angle</div>
                <div style={{ fontSize: 44, fontWeight: 800, color: isSquare ? "#5BD99A" : TOOLS_ACCENT, fontVariantNumeric: "tabular-nums", lineHeight: 1.05 }}>
                  {measuredAngle.toFixed(1)}°
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: isSquare ? "#5BD99A" : "#F5A299", marginTop: 4 }}>
                  {isSquare ? "Square ✓" : `${fromSquare > 0 ? "+" : ""}${fromSquare.toFixed(1)}° from square`}
                </div>
              </div>
              <div style={{ marginTop: 14, display: "flex", gap: 8, justifyContent: "center" }}>
                <button onClick={setReference} style={{ background: "#1C1E2E", color: TOOLS_ACCENT, border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Re-zero
                </button>
                <button onClick={clear} style={{ background: "#1C1E2E", color: "#9A9DBE", border: "none", borderRadius: 8, padding: "10px 14px", fontSize: 13, cursor: "pointer" }}>
                  Clear
                </button>
              </div>
            </>
          )}

          <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 18, textAlign: "center" }}>
            Best with the phone's long edge flush against each surface. For framing or cabinetry, also check with a physical try square — phone sensors aren't calibrated for sub-degree precision.
          </p>
        </>
      )}
    </ToolsShell>
  );
}
